import { useCallback, useMemo } from "react";
import { TableColumn } from "@components/Table/TableColumns";
import { getFormattedThousand, getRem, ID } from "@datassential/platform-ui-lib";
import { useMetrics } from "@core/CommonDataContext";
import { useConsumerGroupsContext } from "@pages/Profile/components/ConsumerGroups/ConsumerGroups";
import { RawNorm } from "@pages/Profile/ProfileContext";
import { compareMetrics, Metric, PROFILE_CONSUMER_GROUPS_METRICS_ORDER } from "@models/Metric";
import { components } from "@api/api";

type MetricModel = NonNullable<components['schemas']["FlavorTracker.Entities.DTO.Common.MetricDataWithOriginalAndNormAndChangeBase"]>;

export type ConsumerGroupsTableItem = {
    id: ID;
    name: string;
    nameTooltipId?: string;
    baseSize: number;
    metrics: {
        id: Metric;
        name: string;
        value: string;
        numValue: number;
    }[];
};

export function useConsumerGroupsTableConfig(isSort: boolean = false) {
    return useCallback(
        (data: ConsumerGroupsTableItem[]): TableColumn[] => [
            {
                key: 'name',
                sortKey: isSort ? 'name' : undefined,
                head: isSort ? 'Name' : undefined,
                headText: isSort ? 'Name' : undefined,
                Value: ({ index }) => <>{data[index]?.name}</>,
                valueText: (index) => data[index]?.name,
                style: {
                    cell: {
                        flex: `0 0 ${getRem(230)}`,
                    },
                },
                accentLevel: 1,
                getHtmlAttributes: (index) => ({
                    'data-tooltip-id': index === undefined ? '' : data[index]?.nameTooltipId,
                }),
            },
            {
                key: 'baseSize',
                sortKey: isSort ? 'baseSize' : undefined,
                head: 'Base',
                headText: 'Base',
                Value: ({ index }) => <>{getFormattedThousand(data[index].baseSize)}</>,
                valueText: (index) => `${data[index].baseSize}`,
                accentLevel: 1,
                style: {
                    cell: {
                        justifyContent: 'center',
                    },
                }
            },
            ...(data[0]?.metrics ?? []).map((metric, metricIndex): TableColumn => ({
                key: metric.name,
                sortKey: isSort ? `metric${metricIndex}` : undefined,
                head: metric.name,
                headText: metric.name,
                Value: ({ index }) => <>{data[index]?.metrics[metricIndex]?.value}</>,
                valueText: (index) => data[index]?.metrics[metricIndex]?.value,
                accentLevel: 1,
                style: {
                    cell: {
                        justifyContent: 'center',
                    },
                }
            })),
        ],
        [isSort],
    );
}

export function useConsumerGroupsTableMetrics() {
    const {
        selectedRawNorm,
    } = useConsumerGroupsContext();

    const rawMetrics = useMetrics();

    const metrics = useMemo(
        () => (rawMetrics ?? [])
            .filter((i) => PROFILE_CONSUMER_GROUPS_METRICS_ORDER.includes(i.id as Metric))
            .sort((a, b) => compareMetrics(PROFILE_CONSUMER_GROUPS_METRICS_ORDER, a.id, b.id)),
        [rawMetrics],
    );

    return useCallback(
        (data: MetricModel[]): ConsumerGroupsTableItem['metrics'] => {
            return metrics.map((metric) => {
                const itemData = data.find((item) => item.metric === metric.id);
                let numValue = itemData?.raw ?? 0;
                let value = `${numValue}%`;

                if (selectedRawNorm === RawNorm.Norm) {
                    numValue = itemData?.norm ?? 0;
                    value = `${numValue}`;
                }

                return {
                    id: metric.id ?? Metric.NoData,
                    name: metric.name ?? '',
                    value,
                    numValue,
                }
            });
        },
        [metrics, selectedRawNorm],
    );
}

export function useGetSelectedChartMetricValue() {
    const { selectedMetric } = useConsumerGroupsContext();

    return useCallback(
        (itemModel: ConsumerGroupsTableItem) => {
            const selectedMetricModel: ConsumerGroupsTableItem['metrics'][number] | undefined = itemModel.metrics.find(
                (metric) => metric.id === selectedMetric
            );

            if (selectedMetricModel) {
                return {
                    value: selectedMetricModel.value,
                    numValue: selectedMetricModel.numValue,
                };
            }

            return {
                value: getFormattedThousand(itemModel.baseSize),
                numValue: itemModel.baseSize,
            };
        },
        [selectedMetric]
    );
}